/**
 * Die Tagesliste (`kal-agenda`, < 768px, Spec 07 „Schmale Ansicht 380px") —
 * sieben Spalten sind dort zu schmal für einen Titel. Ein Kopf je Woche
 * (`kal-woche-<iso_week>`), darunter **nur die Tage mit Einträgen**; ist eine
 * ganze Woche leer, steht unter ihrem Kopf „Nichts." (AK-462).
 *
 * Kein Deckel bei drei Einträgen wie im Raster: Die Zeile ist hier nicht
 * durch 92px Zellhöhe begrenzt, sondern läuft über die volle Breite und
 * wächst mit der Liste — der einzige Grund für `kal-tag-mehr-<datum>` im
 * Raster (Platz in der Zelle) entfällt.
 */

import { CalendarEntryRow } from '@/features/calendar/CalendarEntryRow'
import type { CalendarEntryActions } from '@/features/calendar/CalendarEntryRow'
import type { CalendarDay, CalendarWeek } from '@/features/calendar/api'

interface CalendarAgendaProps {
  readonly weeks: readonly CalendarWeek[]
  readonly actions: CalendarEntryActions
}

export function CalendarAgenda({ weeks, actions }: CalendarAgendaProps) {
  return (
    <div data-testid="kal-agenda" className="flex flex-col">
      {weeks.map((week) => (
        <CalendarAgendaWeek key={`${String(week.iso_year)}-${String(week.iso_week)}`} week={week} actions={actions} />
      ))}
    </div>
  )
}

function CalendarAgendaWeek({ week, actions }: { readonly week: CalendarWeek; readonly actions: CalendarEntryActions }) {
  const daysWithEntries = week.days.filter((day) => day.total > 0)

  return (
    <div className="border-border border-b py-2">
      <div
        data-testid={`kal-woche-${String(week.iso_week)}`}
        className="text-label text-muted flex items-baseline gap-1.5 px-(--page-x) py-1"
      >
        <span data-numeric>{week.week_display}</span>
        <span aria-hidden="true">·</span>
        <span data-numeric>{week.range_display}</span>
      </div>

      {daysWithEntries.length === 0 ? (
        <p className="text-label text-muted px-(--page-x) py-1.5">Nichts.</p>
      ) : (
        daysWithEntries.map((day) => <CalendarAgendaDay key={day.on} day={day} actions={actions} />)
      )}
    </div>
  )
}

function CalendarAgendaDay({ day, actions }: { readonly day: CalendarDay; readonly actions: CalendarEntryActions }) {
  return (
    <div
      data-testid={`kal-tag-${day.on}`}
      className={`flex flex-col gap-1 px-(--page-x) py-1.5 ${day.is_today ? 'border-accent border-l-2' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span className={`text-label ${day.is_weekend ? 'text-muted' : 'text-text'}`}>
          {day.weekday_display} {day.day_display}
        </span>
        {day.is_today && (
          <span data-testid="kal-tag-heute" className="text-label text-accent">
            heute
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        {day.entries.map((entry) => (
          <CalendarEntryRow key={entry.id} entry={entry} actions={actions} size="comfortable" />
        ))}
      </div>
    </div>
  )
}
